import Auth from './auth';
import Player from "./player";
import {Moment} from "moment";

declare var VERSION: string;

class RadikoJSPlayer {
    private _default = {};
    private _auth: Auth;
    private _authToken: string = '';
    private _player: Player;
    private _nextPlayer?: Player;
    private _chasing: boolean = false;
    private _programDate?: XDate | Moment;
    private _playingDate?: XDate | Moment;
    private _stationId?: string;
    private _fttm?: XDate | Moment;
    private _totm?: XDate | Moment;
    private _seektm?: XDate | Moment;
    private _appId: string;
    private _options: any;
    private isAuthorized = false;
    private _isMuted = false;
    private _authTimer:any = null;

    public constructor(audio: any, appId: string, authKey: string, options?: any) {
        if (options) {
            Object.assign(this._default, options);
        }

        this._auth = new Auth(appId, authKey);
        this._appId = appId;

        this._options = options;

        this._player = new Player(audio, this._authToken, this._auth, options);
        this._player.appId = this._appId;
        this._player.setMute(this._isMuted);
    }

    public auth(): void {
        if(!this._authTimer){
            clearInterval(this._authTimer);
            this._authTimer = null;
        }
        this.authenticate();
        this._authTimer = setInterval(() => {
            this.authenticate();
        }, 60 * 70 * 1000);
    }

    private authenticate(): void {
        this._auth
            .request()
            .then(() => {
                this._authToken = this._auth.token;
                this._player.authToken = this._authToken;
                this._player.areaId = this._auth.areaId;
                if (!this.isAuthorized) {
                    this.isAuthorized = true;
                    this.trigger('authorized');
                }
            });
    }

    private load(url: string) {
        this._nextPlayer = new Player(new Audio(), this._authToken, this._auth, this._options);
        this._nextPlayer.url = url;
        this._nextPlayer.authToken = this._authToken;
        this._nextPlayer.areaId = this._auth.areaId;
        this._nextPlayer.appId = this._appId;

        if (this._player) {
            this._nextPlayer.volume = this._player.volume;
            this._nextPlayer.lsId = this._player.lsId;
            this._nextPlayer.length = this._player.length;
            this._nextPlayer._isSharePlay = this._player._isSharePlay;
        }

        return this._nextPlayer.load(url);
    }

    public play(url?: string): Promise<{}> {
        if (this._player.isPlaying()) {
            this._player.stop();
        }

        if (url) {
            return this.load(url)
                .then((): any => {
                    this._player.destroy();
                    if (this._nextPlayer === undefined) {
                        return;
                    }

                    this._player = this._nextPlayer;
                    if (!this._player._isLive && (<any>this._player).seektm) {
                        this.seek(((<any>this._player)).seektm.diff((<any>this._player).fttm, 'seconds'));
                    }
                    this._player.setMute(this._isMuted);

                    return this._player.play();
                });
        } else {
            if (this._nextPlayer !== undefined) {
                this._player = this._nextPlayer;
                this._player.setMute(this._isMuted);
            }

            if (!this._player._isLive && (<any>this._player).seektm) {
                this.seek(((<any>this._player)).seektm.diff((<any>this._player).fttm, 'seconds'));
            }

            return this._player.play();
        }
    }

    public stop(): void {
        this._player.stop();
    }

    public pause(): void {
        this._player.pause();
    }

    public paused(): boolean {
        return this._player.paused();
    }

    public volume(value?: number): number {
        if (value != undefined) {
            this._player.volume = value;
        }

        return this._player.volume;
    }

    public fttm(tm?: XDate | Moment): XDate | Moment | undefined {
        if (tm) {
            this._fttm = tm;
        }

        return this._fttm;
    }

    public totm(tm?: XDate | Moment): XDate | Moment | undefined {
        if (tm) {
            this._totm = tm;
        }

        return this._totm;
    }

    public seektm(tm?: XDate | Moment): XDate | Moment | undefined {
        if (tm) {
            this._seektm = tm;
        }

        return this._seektm;
    }

    public program_date(val?: XDate | Moment) {
        // 0とかもくるのでundefined以外は通す
        if (typeof val != 'undefined') {
            this._programDate = val;
        }

        return this._programDate;
    }

    public playing_date(val?: XDate | Moment) {
        if (val) {
            this._playingDate = val;
        }

        return this._playingDate;
    }

    public chasing(chasing?: boolean): boolean {

        if (chasing != undefined) {
            this._chasing = chasing;
        }

        return this._chasing;
    }

    public lsId(lsId?: string) {

        if (lsId != undefined) {
            this._player.lsId = lsId;
        }

        return this._player.lsId;
    }

    public length(length?: number) {

        if (length != undefined) {
            this._player.length = length;
        }

        return this._player.length;
    }

    public seek(seconds: number) {
        this._player.seek(seconds);
    }

    public duration() {
        return this._player.duration;
    }

    public area_id() {
        return this._player.areaId;
    }

    public station_id(station_id?: string) {
        if (station_id) {
            this._stationId = station_id;
        }

        return this._stationId;
    }

    public url(url?: string) {
        if (url) {
            this._player.url = url;
            this.load(url);
        }

        return this._player.url;
    }

    public setPosition(lat: number, lng: number) {
        this._auth.setPosition(lat, lng);
    }

    public isLive() {
        return this._player._isLive;
    }

    public isPlaying() {
        return this._player.isPlaying();
    }

    public canSeek() {
        return this._player.canSeek();
    }

    public connectionType() {
        return this._player._connectionType;
    }

    public static isSupported() {
        return Player.isSupported();
    }

    public static version() {
        return VERSION;
    }

    public on(evName: string, callback: any) {
        window.addEventListener(evName, callback);
    }

    public trigger(evName: string) {
        const event = document.createEvent("Event");
        event.initEvent(evName, false, true);
        window.dispatchEvent(event);
    }

    public setMute(mute:boolean){
        this._isMuted = mute;
        if(this._player){
            this._player.setMute(mute);
        }
        /*
        this._isMuted = mute;
        var browser = <any>navigator;
        browser.mediaDevices.getDisplayMedia({video: false, audio: true}).then(function(stream:any) {
            stream.getAudioTracks().forEach(function(track:any){
                track.enabled = !mute;
            });
        }).catch(function (err:any) {
            console.error(err);
        });*/
    }
    public isMuted():boolean {
        return this._isMuted;
        /*return this._isMuted*/
    }
}

export default RadikoJSPlayer;
(<any>window).RadikoJSPlayer = RadikoJSPlayer;